import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { Textarea } from '../common/Textarea';
import { ErrorMessage } from '../common/ErrorMessage';
import { sendMessage } from '../../api/messages';
import type { Comment } from '../../types/comment';
import { formatRelativeTime } from '../../utils/dateUtils';
import type { SendMessageRequest } from '../../types/message';
import { useLanguage } from '../../i18n/useLanguage';

interface MessageCommentAuthorModalProps {
  isOpen: boolean;
  onClose: () => void;
  comment: Comment;
  postTitle?: string;
}

export const MessageCommentAuthorModal: React.FC<MessageCommentAuthorModalProps> = ({
  isOpen,
  onClose,
  comment,
  postTitle,
}) => {
  const { language } = useLanguage();
  const [messageContent, setMessageContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens/closes or comment changes
  useEffect(() => {
    if (isOpen) {
      setMessageContent('');
      setError(null);
    }
  }, [isOpen, comment]);

  const handleSendMessage = async () => {
    if (!messageContent.trim()) {
      setError('Please enter a message');
      return;
    }

    if (!comment.authorId) {
      setError('Cannot send message: comment author ID is missing');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const request: SendMessageRequest = {
        content: messageContent.trim(),
        recipientId: comment.authorId,
      };

      await sendMessage(request);

      // Reset and close
      setMessageContent('');
      setError(null);
      onClose();
    } catch (err: any) {
      console.error('Failed to send message:', err);
      setError(
        err.response?.data?.message || err.message || 'Failed to send message. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setMessageContent('');
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Message Comment Author" size="medium">
      <div className="message-comment-author-modal">
        <div className="message-comment-author-comment-preview">
          <div className="message-comment-author-comment-header">
            <p className="message-comment-author-comment-label">Comment by {comment.authorUsername || 'Comment author'}:</p>
            {postTitle && (
              <p className="message-comment-author-post-context">
                On post: "{postTitle}"
              </p>
            )}
          </div>
          <div className="message-comment-author-comment-content">
            <p>{comment.content}</p>
            <div className="message-comment-author-comment-meta">
              <span className="message-comment-author-comment-time">
                {formatRelativeTime(comment.createdAt, language)}
              </span>
            </div>
          </div>
        </div>

        <div className="message-comment-author-form">
          <div className="message-comment-author-selected">
            <p className="message-comment-author-selected-label">
              Sending message to: <strong>{comment.authorUsername || 'Comment author'}</strong>
            </p>
          </div>
          <Textarea
            label="Your message"
            value={messageContent}
            onChange={(e) => {
              setMessageContent(e.target.value);
              setError(null);
            }}
            placeholder="Type your message here..."
            rows={6}
            error={error || undefined}
            disabled={isSubmitting}
          />
          {error && <ErrorMessage message={error} />}
          <div className="message-comment-author-actions">
            <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSendMessage}
              disabled={isSubmitting || !messageContent.trim()}
              isLoading={isSubmitting}
            >
              Send Message
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
